import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Send, Mic, ImagePlus, Volume2, Phone, Video, Bot, User,
  X, MicOff, RefreshCw, ScanLine, Copy, PhoneOff, Camera,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";
import { toast } from "sonner";

const GEMINI_MODEL = "gemini-2.5-flash";
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;
const SLASH_CMDS = ["/fan off", "/fan auto", "/light on", "/temp check", "/gas check", "/cam snap", "/pir on"];

function parseAIResponse(data) {
  if (data.candidates?.[0]) return data.candidates[0].content.parts[0].text;
  if (data.choices?.[0]) return data.choices[0].message.content;
  return "No response received.";
}

function extractCmds(text) {
  const re = /\[CTRL:(\w+)=(\w+)\]/g;
  const cmds = [];
  let m;
  while ((m = re.exec(text)) !== null) cmds.push([m[1], m[2]]);
  return { cmds, clean: text.replace(/\[CTRL:[\w]+=[\w]+\]/g, "").trim() };
}

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function Bubble({ m }) {
  const isAI = m.role === "ai";
  return (
    <div className={`flex gap-2.5 ${isAI ? "" : "flex-row-reverse"}`}>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${isAI ? "bg-[var(--accent-soft)] text-[var(--accent)]" : "bg-[var(--surface2)] text-[var(--t2)]"}`}>
        {isAI ? <Bot size={16} /> : <User size={16} />}
      </div>
      <div className={`max-w-[75%] px-4 py-2.5 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${isAI ? "bg-[var(--surface2)] border border-[var(--bd)]" : "bg-[var(--accent)] text-black"}`}>
        {m.img && <img src={m.img} alt="attach" className="rounded-md mb-2 max-h-40 block" />}
        {m.text}
      </div>
    </div>
  );
}

function CallTimer({ active }) {
  const [s, setS] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setS((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
  return (
    <span className="font-mono text-sm" data-testid="call-timer">
      {String(Math.floor(s / 60)).padStart(2, "0")}:{String(s % 60).padStart(2, "0")}
    </span>
  );
}

export default function AiChat() {
  const { telemetry, settings, sendCommand, sendCamCommand, camImage, addSnapshot } = useApp();

  const [messages, setMessages] = useState([{
    role: "ai",
    text: "Welcome to the Campus Energy AI assistant. Ask about live readings, control devices, or use slash commands below.",
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [voiceCall, setVoiceCall] = useState(false);
  const [videoCall, setVideoCall] = useState(false);
  const [muted, setMuted] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [facing, setFacing] = useState("user");

  const fileRef = useRef(null);
  const endRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Refs for things used inside async callbacks to avoid stale closures
  const chatHistRef = useRef([]);
  const gemKeyIdxRef = useRef(0);
  const ttsSidRef = useRef(0);
  const ttsAudioRef = useRef(null);
  const isAISpeakingRef = useRef(false);
  const isProcessingRef = useRef(false);
  const continuousRecRef = useRef(null);
  const lastTranscriptRef = useRef("");
  const lastResponseTimeRef = useRef(0);
  const voiceCallRef = useRef(false);
  const mutedRef = useRef(false);
  const settingsRef = useRef(settings);
  const telemetryRef = useRef(telemetry);

  // Keep refs in sync
  useEffect(() => { voiceCallRef.current = voiceCall; }, [voiceCall]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { telemetryRef.current = telemetry; }, [telemetry]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // ── System prompt with live telemetry ──
  const buildSysPrompt = useCallback(() => {
    const t = telemetryRef.current;
    const s = settingsRef.current;
    const lp = t ? Math.round(((t.light ?? 0) / 255) * 100) : 0;
    return (
      `You are Energy AI, assistant for the Campus Energy Monitor cloud dashboard (CPUT Final Year Project).\n` +
      `LIVE ESP32 STATE (via MQTT):\n` +
      `  Temperature : ${t?.temperature ?? "--"}°C\n` +
      `  Fan         : ${t?.fan_speed ?? 0}% [${t?.fan_auto ? "AUTO temp-control" : "MANUAL"}]\n` +
      `  PIR motion  : ${t?.pir ? "DETECTED" : "Idle"}\n` +
      `  Light (PWM) : ${lp}%\n` +
      `  Light hub G32: ${t?.ir ? "ON" : "OFF"} (PWM via TIP120)\n` +
      `  WiFi        : ${t?.wifi ? `Connected - IP ${t.ip ?? ""}` : "Disconnected"}\n` +
      `  Uptime      : ${Math.floor((t?.uptime ?? 0) / 60)} min\n` +
      `  Air quality : ${t?.gas_quality ?? "--"}${t?.gas_ppm != null ? " - " + Math.round(t.gas_ppm) + " ppm" : ""} (MQ-135)\n` +
      `PZEM L1: ${t?.phases?.L1 ? `${t.phases.L1.voltage}V  ${t.phases.L1.current}A  ${t.phases.L1.power}W  PF=${t.phases.L1.pf}  ${t.phases.L1.frequency}Hz` : "not connected"}\n` +
      `PZEM L2: ${t?.phases?.L2 ? `${t.phases.L2.voltage}V  ${t.phases.L2.current}A  ${t.phases.L2.power}W  PF=${t.phases.L2.pf}  ${t.phases.L2.frequency}Hz` : "not connected"}\n` +
      `PZEM L3: ${t?.phases?.L3 ? `${t.phases.L3.voltage}V  ${t.phases.L3.current}A  ${t.phases.L3.power}W  PF=${t.phases.L3.pf}  ${t.phases.L3.frequency}Hz` : "not connected"}\n` +
      `FAN THRESHOLDS: 30%=${s.fan30}°C | 60%=${s.fan60}°C | 100%=${s.fan100}°C | Critical=${s.fanCritical}°C\n` +
      `Reply concisely. 2–3 sentences max unless user asks for detail.\n` +
      `HARDWARE CONTROL via MQTT - embed EXACT tokens when user asks to control hardware (auto-executed, hidden):\n` +
      `[CTRL:fan=off]  [CTRL:fan=low]  [CTRL:fan=med]  [CTRL:fan=high]  [CTRL:fan=auto]\n` +
      `[CTRL:light=on] [CTRL:light=off]\n` +
      `[CTRL:led=on]   [CTRL:led=off]\n` +
      `[CTRL:buzzer=1] [CTRL:buzzer=2] [CTRL:buzzer=3] [CTRL:buzzer=4] [CTRL:buzzer=5]\n` +
      `[CTRL:all=off]  (fan + light + LED all off)\n` +
      `[CTRL:cam=snap] (camera snapshot)\n` +
      `Only embed a token when user explicitly asks to control hardware. Always confirm what you did.`
    );
  }, []);

  // ── Execute [CTRL:device=action] tokens from AI response ──
  const executeCtrl = useCallback((cmds) => {
    cmds.forEach(([device, action]) => {
      if (device === "fan")    sendCommand("fan",    action,    `Fan ${action}`);
      else if (device === "light")  sendCommand("light",  action,    `Light ${action}`);
      else if (device === "led")    sendCommand("ir",     action,    `LED ${action}`);
      else if (device === "buzzer") sendCommand("buzzer", action,    `Buzzer ×${action}`);
      else if (device === "all")    sendCommand("system", "all_off", "ALL OFF");
      else if (device === "cam")    sendCamCommand(action);
    });
  }, [sendCommand, sendCamCommand]);

  // ── Execute slash commands ──
  const execSlash = useCallback((text) => {
    const t = text.toLowerCase();
    const map = [
      ["/fan off",    () => sendCommand("fan",    "off",  "Fan OFF")],
      ["/fan low",    () => sendCommand("fan",    "30",   "Fan 30%")],
      ["/fan med",    () => sendCommand("fan",    "60",   "Fan 60%")],
      ["/fan high",   () => sendCommand("fan",    "100",  "Fan 100%")],
      ["/fan auto",   () => sendCommand("fan",    "auto", "Fan AUTO")],
      ["/light on",   () => sendCommand("light",  "on",   "Light ON")],
      ["/light off",  () => sendCommand("light",  "off",  "Light OFF")],
      ["/cam snap",   () => sendCamCommand("snapshot")],
      ["/pir on",     () => sendCamCommand("pir:on")],
      ["/pir off",    () => sendCamCommand("pir:off")],
    ];
    let found = false;
    map.forEach(([cmd, fn]) => { if (t.includes(cmd)) { fn(); found = true; } });
    return found;
  }, [sendCommand, sendCamCommand]);

  // ── TTS - shared audio element ──
  const getTtsAudio = useCallback(() => {
    if (!ttsAudioRef.current) ttsAudioRef.current = new Audio();
    return ttsAudioRef.current;
  }, []);

  const stopCurrentTTS = useCallback(() => {
    ttsSidRef.current++;
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    const a = ttsAudioRef.current;
    if (a) { a.onended = null; a.onerror = null; a.pause(); try { a.src = ""; } catch (_) {} }
  }, []);

  // ── TTS provider implementations (all read from settingsRef to avoid stale closures) ──

  const speakBrowser = useCallback((text, onComplete) => {
    if (!window.speechSynthesis) { onComplete?.(); return; }
    const u = new SpeechSynthesisUtterance(text);
    u.rate = settingsRef.current.speechRate ?? 1;
    u.onend = () => onComplete?.();
    u.onerror = () => onComplete?.();
    window.speechSynthesis.speak(u);
  }, []);

  const speakStreamElements = useCallback((text, onComplete, sid) => {
    if (ttsSidRef.current !== sid) return;
    const a = getTtsAudio();
    a.src = `https://api.streamelements.com/kappa/v2/speech?voice=Brian&text=${encodeURIComponent(text.substring(0, 300))}`;
    a.onended = () => onComplete?.();
    a.onerror = () => { if (ttsSidRef.current === sid) speakBrowser(text, onComplete); };
    a.play().catch(() => { if (ttsSidRef.current === sid) speakBrowser(text, onComplete); });
  }, [getTtsAudio, speakBrowser]);

  const speakVoiceRSS = useCallback((text, onComplete, sid) => {
    if (ttsSidRef.current !== sid) return;
    const { voicerssKey: key, voicerssLang: lang = "en-us", voicerssVoice: voice = "Linda" } = settingsRef.current;
    if (!key) { speakStreamElements(text, onComplete, sid); return; }
    const a = getTtsAudio();
    a.src = `https://api.voicerss.org/?key=${encodeURIComponent(key)}&hl=${encodeURIComponent(lang)}&v=${encodeURIComponent(voice)}&f=16khz_16bit_mono&src=${encodeURIComponent(text.substring(0, 500))}`;
    a.onended = () => onComplete?.();
    a.onerror = () => { if (ttsSidRef.current === sid) speakStreamElements(text, onComplete, sid); };
    a.play().catch(() => { if (ttsSidRef.current === sid) speakStreamElements(text, onComplete, sid); });
  }, [getTtsAudio, speakStreamElements]);

  const speakAzure = useCallback((text, onComplete, sid) => {
    if (ttsSidRef.current !== sid) return;
    const { azureKey: key, azureRegion: region = "southafricanorth", azureVoice: voiceName = "en-US-JennyNeural", speechRate = 1 } = settingsRef.current;
    if (!key) { speakStreamElements(text, onComplete, sid); return; }
    const rate = speechRate >= 1 ? `+${Math.round((speechRate - 1) * 100)}%` : `${Math.round((speechRate - 1) * 100)}%`;
    const ssml = `<speak version="1.0" xml:lang="en-US"><voice name="${voiceName}"><prosody rate="${rate}">${text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</prosody></voice></speak>`;
    fetch(`https://${region}.tts.speech.microsoft.com/cognitiveservices/v1`, {
      method: "POST",
      headers: { "Ocp-Apim-Subscription-Key": key, "Content-Type": "application/ssml+xml", "X-Microsoft-OutputFormat": "audio-16khz-32kbitrate-mono-mp3" },
      body: ssml,
    })
      .then((r) => { if (!r.ok) throw new Error("Azure " + r.status); return r.blob(); })
      .then((blob) => {
        if (ttsSidRef.current !== sid) return;
        const url = URL.createObjectURL(blob);
        const a = getTtsAudio(); a.src = url;
        a.onended = () => { URL.revokeObjectURL(url); onComplete?.(); };
        a.onerror = () => { URL.revokeObjectURL(url); if (ttsSidRef.current === sid) speakStreamElements(text, onComplete, sid); };
        a.play().catch(() => { URL.revokeObjectURL(url); if (ttsSidRef.current === sid) speakStreamElements(text, onComplete, sid); });
      })
      .catch(() => { if (ttsSidRef.current === sid) speakStreamElements(text, onComplete, sid); });
  }, [getTtsAudio, speakStreamElements]);

  const speakElevenLabs = useCallback((text, onComplete, sid) => {
    if (ttsSidRef.current !== sid) return;
    const { elevenKey: key, elevenVoiceId: voiceId = "21m00Tcm4TlvDq8ikWAM" } = settingsRef.current;
    if (!key) { speakAzure(text, onComplete, sid); return; }
    fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
      method: "POST",
      headers: { "xi-api-key": key, "Content-Type": "application/json", Accept: "audio/mpeg" },
      body: JSON.stringify({ text, model_id: "eleven_monolingual_v1", voice_settings: { stability: 0.5, similarity_boost: 0.75 } }),
    })
      .then((r) => { if (!r.ok) throw new Error("ElevenLabs " + r.status); return r.blob(); })
      .then((blob) => {
        if (ttsSidRef.current !== sid) return;
        const url = URL.createObjectURL(blob);
        const a = getTtsAudio(); a.src = url;
        a.onended = () => { URL.revokeObjectURL(url); onComplete?.(); };
        a.onerror = () => { URL.revokeObjectURL(url); if (ttsSidRef.current === sid) speakAzure(text, onComplete, sid); };
        a.play().catch(() => { URL.revokeObjectURL(url); if (ttsSidRef.current === sid) speakAzure(text, onComplete, sid); });
      })
      .catch(() => { if (ttsSidRef.current === sid) speakAzure(text, onComplete, sid); });
  }, [getTtsAudio, speakAzure]);

  const speakWithTTS = useCallback((text, onComplete) => {
    if (!text) { onComplete?.(); return; }
    stopCurrentTTS();
    const sid = ttsSidRef.current;
    // Always try best quality first, cascade down on failure:
    // ElevenLabs → Azure → StreamElements → VoiceRSS → Browser TTS
    speakElevenLabs(text, onComplete, sid);
  }, [stopCurrentTTS, speakElevenLabs]);

  // ── Gemini API with key rotation → DeepSeek fallback ──
  const callGemini = useCallback(async (text, image = null) => {
    const s = settingsRef.current;
    const keys = (s.geminiKeys || "").split("\n").map((k) => k.trim()).filter(Boolean);
    const dsKey = s.deepseekKey;
    const sysP = buildSysPrompt();

    const callDeepSeek = async (msgs) => {
      if (!dsKey) throw new Error("No DeepSeek key configured");
      const r = await fetch("https://api.deepseek.com/chat/completions", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + dsKey },
        body: JSON.stringify({ model: "deepseek-chat", messages: msgs, max_tokens: 280, temperature: 0.5 }),
      });
      if (!r.ok) throw new Error("DeepSeek HTTP " + r.status);
      return r.json();
    };

    const toDeepSeekHist = () =>
      chatHistRef.current.slice(-12).map((m) => ({ role: m.role === "model" ? "assistant" : "user", content: m.parts[0].text }));

    // Only DeepSeek available
    if (!keys.length) {
      return callDeepSeek([{ role: "system", content: sysP }, ...toDeepSeekHist(), ...(text ? [{ role: "user", content: text }] : [])]);
    }

    let idx = gemKeyIdxRef.current % keys.length;
    for (let attempt = 0; attempt <= keys.length; attempt++) {
      if (attempt === keys.length) {
        if (dsKey) {
          toast.info("All Gemini keys rate-limited - switching to DeepSeek");
          return callDeepSeek([{ role: "system", content: sysP }, ...toDeepSeekHist(), ...(text ? [{ role: "user", content: text }] : [])]);
        }
        throw new Error("All Gemini keys exhausted - add more keys or a DeepSeek key in Settings");
      }

      let body;
      if (image) {
        const base64 = await fileToBase64(image);
        body = {
          contents: [{ parts: [{ text: sysP + "\n\nUser: " + (text || "Analyze this image.") }, { inline_data: { mime_type: image.type, data: base64 } }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 400 },
        };
      } else {
        body = {
          system_instruction: { parts: [{ text: sysP }] },
          contents: [...chatHistRef.current.slice(-12), ...(text ? [{ role: "user", parts: [{ text }] }] : [])],
          generationConfig: { temperature: 0.5, maxOutputTokens: 280 },
        };
      }

      const r = await fetch(GEMINI_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-goog-api-key": keys[idx] },
        body: JSON.stringify(body),
      });

      if (r.status === 429 || r.status === 503 || r.status === 403) {
        toast.info(`Gemini key ${idx + 1}/${keys.length} rate limited - trying next`);
        idx = (idx + 1) % keys.length;
        gemKeyIdxRef.current = idx;
        continue;
      }
      if (!r.ok) throw new Error("Gemini HTTP " + r.status);
      gemKeyIdxRef.current = idx;
      return r.json();
    }
  }, [buildSysPrompt]);

  // ── Voice call input handler ──
  const handleCallInput = useCallback(async (text) => {
    if (isProcessingRef.current) return;
    setTranscript((t) => [...t, { who: "You", text }]);
    isProcessingRef.current = true;
    stopCurrentTTS();
    if (continuousRecRef.current) { try { continuousRecRef.current.stop(); } catch (_) {} }
    try {
      const data = await callGemini(text);
      const raw = parseAIResponse(data);
      const { cmds, clean } = extractCmds(raw);
      if (cmds.length) executeCtrl(cmds);
      const reply = clean || raw;
      setTranscript((t) => [...t, { who: "AI", text: reply }]);
      isAISpeakingRef.current = true;
      speakWithTTS(reply, () => {
        isAISpeakingRef.current = false;
        isProcessingRef.current = false;
        if (voiceCallRef.current && !mutedRef.current && continuousRecRef.current) {
          setTimeout(() => { try { continuousRecRef.current?.start(); } catch (_) {} }, 500);
        }
      });
    } catch (err) {
      setTranscript((t) => [...t, { who: "AI", text: "Error: " + err.message }]);
      isProcessingRef.current = false;
      isAISpeakingRef.current = false;
    }
  }, [callGemini, executeCtrl, speakWithTTS, stopCurrentTTS]);

  // ── Continuous speech recognition ──
  const createContinuousRec = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;
    const rec = new SR();
    rec.continuous = true; rec.interimResults = false; rec.lang = "en-US";
    rec.onresult = (ev) => {
      if (!voiceCallRef.current || mutedRef.current || isAISpeakingRef.current || isProcessingRef.current) return;
      const last = ev.results[ev.results.length - 1];
      if (!last.isFinal) return;
      const text = last[0].transcript.trim();
      const now = Date.now();
      if (text.length > 2 && text !== lastTranscriptRef.current && now - lastResponseTimeRef.current > 3000) {
        lastTranscriptRef.current = text;
        lastResponseTimeRef.current = now;
        setTimeout(() => handleCallInput(text), 300);
      }
    };
    rec.onerror = (ev) => {
      if (ev.error === "not-allowed") { setVoiceCall(false); toast.error("Microphone access denied"); return; }
      if (voiceCallRef.current && !mutedRef.current && !isAISpeakingRef.current && !isProcessingRef.current) {
        setTimeout(() => { try { rec.start(); } catch (_) {} }, 1500);
      }
    };
    rec.onend = () => {
      if (voiceCallRef.current && !mutedRef.current && !isAISpeakingRef.current && !isProcessingRef.current) {
        setTimeout(() => { try { rec.start(); } catch (_) {} }, 1200);
      }
    };
    return rec;
  }, [handleCallInput]);

  // ── Open voice call ──
  const openVoice = async () => {
    const s = settingsRef.current;
    const keys = (s.geminiKeys || "").split("\n").map((k) => k.trim()).filter(Boolean);
    if (!keys.length && !s.deepseekKey) { toast.error("Add Gemini or DeepSeek key in Settings first"); return; }
    try {
      const st = await navigator.mediaDevices.getUserMedia({ audio: true });
      st.getTracks().forEach((t) => t.stop());
    } catch { toast.error("Microphone access denied"); return; }

    isAISpeakingRef.current = false;
    isProcessingRef.current = false;
    lastTranscriptRef.current = "";
    lastResponseTimeRef.current = 0;
    setMuted(false);
    setVoiceCall(true);
    const greeting = "Hi! I'm Energy AI. I have live access to your energy monitor via MQTT. How can I help?";
    setTranscript([{ who: "AI", text: greeting }]);

    const rec = createContinuousRec();
    continuousRecRef.current = rec;

    isAISpeakingRef.current = true;
    speakWithTTS(greeting, () => {
      isAISpeakingRef.current = false;
      if (voiceCallRef.current && rec) setTimeout(() => { try { rec.start(); } catch (_) {} }, 500);
    });
  };

  const endCall = useCallback(() => {
    setVoiceCall(false);
    stopCurrentTTS();
    isAISpeakingRef.current = false;
    isProcessingRef.current = false;
    if (continuousRecRef.current) { try { continuousRecRef.current.stop(); } catch (_) {} continuousRecRef.current = null; }
  }, [stopCurrentTTS]);

  // ── Send chat message ──
  const send = useCallback(async (txt) => {
    const text = (txt ?? input).trim();
    if (!text && !selectedImage) return;
    setInput("");
    const img = selectedImage;
    setSelectedImage(null);
    if (selectedImageUrl) { URL.revokeObjectURL(selectedImageUrl); setSelectedImageUrl(null); }

    const previewUrl = img ? URL.createObjectURL(img) : null;
    setMessages((m) => [...m, { role: "user", text: text || "[Image]", ...(previewUrl ? { img: previewUrl } : {}) }]);

    // Slash commands don't need AI (except /gas and /temp which query live sensor data)
    if (!img && text.startsWith("/") && !text.startsWith("/gas") && !text.startsWith("/temp")) {
      const found = execSlash(text);
      setMessages((m) => [...m, { role: "ai", text: found ? `✓ Command sent: ${text} - published to ESP32 via MQTT.` : "Unknown slash command. Try /fan off, /light on, /temp check, /gas check, /cam snap." }]);
      return;
    }

    const s = settingsRef.current;
    const keys = (s.geminiKeys || "").split("\n").map((k) => k.trim()).filter(Boolean);
    if (!keys.length && !s.deepseekKey) {
      setMessages((m) => [...m, { role: "ai", text: "No AI key configured. Go to Settings → AI Keys and add a Gemini API key." }]);
      return;
    }

    if (text && !img) chatHistRef.current.push({ role: "user", parts: [{ text }] });
    setLoading(true);
    try {
      const data = await callGemini(text, img);
      const raw = parseAIResponse(data);
      const { cmds, clean } = extractCmds(raw);
      if (cmds.length) executeCtrl(cmds);
      const reply = clean || raw;
      if (!img) {
        chatHistRef.current.push({ role: "model", parts: [{ text: reply }] });
        if (chatHistRef.current.length > 24) chatHistRef.current = chatHistRef.current.slice(-24);
      }
      setMessages((m) => [...m, { role: "ai", text: reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "ai", text: "Error: " + err.message }]);
    } finally {
      setLoading(false);
    }
  }, [input, selectedImage, selectedImageUrl, execSlash, callGemini, executeCtrl]);

  // ── Mic button (single utterance) ──
  const onMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.info("Speech recognition requires Chrome or Edge"); return; }
    const rec = new SR(); rec.lang = "en-US"; rec.continuous = false; rec.interimResults = false;
    rec.onresult = (e) => { setInput(e.results[0][0].transcript); };
    rec.onerror = () => toast.error("Mic error");
    rec.start(); toast.info("Listening… speak now");
  };

  // ── Image attach ──
  const onAttach = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (selectedImageUrl) URL.revokeObjectURL(selectedImageUrl);
    setSelectedImage(f);
    setSelectedImageUrl(URL.createObjectURL(f));
    toast.success("Image attached - send to analyze with Gemini Vision");
    e.target.value = "";
  };

  // ── Attach latest ESP32 camera image for AI analysis ──
  const useEsp32Cam = useCallback(() => {
    if (!camImage) { toast.info("No ESP32 camera image yet - trigger a snapshot first"); return; }
    const byteStr = atob(camImage.split(",")[1]);
    const arr = new Uint8Array(byteStr.length);
    for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i);
    const file = new File([arr], "esp32-cam.jpg", { type: "image/jpeg" });
    if (selectedImageUrl) URL.revokeObjectURL(selectedImageUrl);
    setSelectedImage(file);
    setSelectedImageUrl(camImage);
    toast.success("ESP32 camera photo attached - send to analyze with Gemini Vision");
  }, [camImage, selectedImageUrl]);

  // ── Video call ──
  const startVideo = async (mode = facing) => {
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch { toast.error("Camera access denied"); }
  };

  const openVideo = () => {
    setVideoCall(true);
    setTranscript([{ who: "AI", text: "Video call connected. Point the camera at equipment - tap Analyze Frame for Gemini Vision analysis." }]);
    setTimeout(() => startVideo(), 300);
  };

  const closeVideo = () => {
    setVideoCall(false);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  };

  const flip = () => {
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    startVideo(next);
  };

  const analyzeFrame = async () => {
    if (!videoRef.current?.videoWidth) { setTranscript((t) => [...t, { who: "AI", text: "Camera feed not ready - wait a moment." }]); return; }
    setTranscript((t) => [...t, { who: "AI", text: "Analyzing frame with Gemini Vision…" }]);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
      addSnapshot(dataUrl);
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "frame.jpg", { type: "image/jpeg" });
      const data = await callGemini("Analyze this live camera frame from the energy monitoring lab. Describe what you see and note anything relevant to equipment status or energy management.", file);
      setTranscript((t) => { const a = [...t]; a[a.length - 1] = { who: "AI", text: parseAIResponse(data) }; return a; });
    } catch (err) {
      setTranscript((t) => { const a = [...t]; a[a.length - 1] = { who: "AI", text: "Analysis error: " + err.message }; return a; });
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCurrentTTS();
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (continuousRecRef.current) { try { continuousRecRef.current.stop(); } catch (_) {} }
    };
  }, [stopCurrentTTS]);

  const lastAiText = [...messages].reverse().find((m) => m.role === "ai")?.text || "";

  return (
    <div className="space-y-4">
      <div>
        <Overline>Conversational AI</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Energy Assistant</h2>
        <p className="text-xs text-[var(--t2)] font-mono mt-1">
          Powered by Gemini 2.5 Flash · key rotation · DeepSeek fallback · TTS chain
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          data-testid="ai-voice-call"
          onClick={openVoice}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm hover:border-[var(--accent)] transition-colors"
        >
          <Phone size={15} className="text-[var(--accent)]" /> Voice Call
        </button>
        <button
          data-testid="ai-video-call"
          onClick={openVideo}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm hover:border-[var(--accent)] transition-colors"
        >
          <Video size={15} className="text-[var(--accent)]" /> Video Call
        </button>
      </div>

      <Panel className="flex flex-col h-[520px]">
        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ai-chat-window">
          {messages.map((m, i) => <Bubble key={i} m={m} />)}
          {loading && (
            <div className="flex gap-2.5">
              <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 bg-[var(--accent-soft)] text-[var(--accent)]"><Bot size={16} /></div>
              <div className="px-4 py-2.5 rounded-lg bg-[var(--surface2)] border border-[var(--bd)] text-sm flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[var(--bd)] p-3">
          {selectedImageUrl && (
            <div className="mb-2 flex items-center gap-2">
              <img src={selectedImageUrl} alt="attach" className="h-10 rounded border border-[var(--bd)]" />
              <span className="text-xs text-[var(--t2)] font-mono">Image attached</span>
              <button onClick={() => { setSelectedImage(null); setSelectedImageUrl(null); }} className="ml-auto text-[var(--t2)] hover:text-[var(--danger)]"><X size={14} /></button>
            </div>
          )}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button data-testid="ai-mic" onClick={onMic} className="w-9 h-9 sm:w-10 sm:h-10 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors shrink-0"><Mic size={16} /></button>
            <button data-testid="ai-attach" onClick={() => fileRef.current?.click()} title="Attach image file" className="hidden sm:flex w-10 h-10 rounded-md border border-[var(--bd)] items-center justify-center hover:border-[var(--bd-strong)] transition-colors shrink-0"><ImagePlus size={17} /></button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAttach} />
            <button data-testid="ai-esp32-cam" onClick={useEsp32Cam} title="Use latest ESP32 camera photo" className={`hidden sm:flex w-10 h-10 rounded-md border items-center justify-center transition-colors shrink-0 ${camImage ? "border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent-soft)]" : "border-[var(--bd)] text-[var(--t2)] opacity-40 cursor-not-allowed"}`}><Camera size={17} /></button>
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !loading && send()}
              placeholder="Ask about energy, or type a /command…"
              disabled={loading}
              className="flex-1 min-w-0 bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2 sm:py-2.5 text-sm focus:outline-none focus:border-[var(--accent)] transition-colors disabled:opacity-50"
            />
            <button
              data-testid="ai-speak"
              onClick={() => speakWithTTS(lastAiText)}
              className="hidden sm:flex w-10 h-10 rounded-md border border-[var(--bd)] items-center justify-center hover:border-[var(--bd-strong)] transition-colors shrink-0"
            >
              <Volume2 size={17} />
            </button>
            <button
              data-testid="ai-send"
              onClick={() => send()}
              disabled={loading}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-[var(--accent)] text-black flex items-center justify-center active:scale-95 transition-transform shrink-0 disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </div>
          <div className="mt-2 text-xs text-[var(--t2)] font-mono flex flex-wrap gap-x-3 gap-y-1" data-testid="slash-hints">
            <span className="text-[var(--t2)]">Slash:</span>
            {SLASH_CMDS.map((c) => (
              <button key={c} onClick={() => send(c)} className="text-[var(--accent)] hover:underline">{c}</button>
            ))}
          </div>
        </div>
      </Panel>

      {/* VOICE CALL OVERLAY */}
      {voiceCall && (
        <div className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-center p-6" data-testid="voice-call-overlay">
          <div className="absolute top-6 left-0 right-0 flex justify-center">
            <span className="px-3 py-1 rounded-full bg-[var(--accent)]/20 text-[var(--accent)] font-mono text-xs flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[var(--accent)] pulse-dot" /> CONNECTED · <CallTimer active />
            </span>
          </div>
          <div className="w-32 h-32 rounded-full bg-[var(--accent)]/10 border-2 border-[var(--accent)] flex items-center justify-center mb-6">
            <Bot size={56} className="text-[var(--accent)]" />
          </div>
          <div className="text-white font-heading font-bold text-xl mb-1">Energy AI</div>
          <div className="text-[var(--accent)] font-mono text-xs mb-6">
            {muted ? "MUTED" : isAISpeakingRef.current ? "SPEAKING…" : "LISTENING…"}
          </div>
          <div className="w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-lg p-4 h-40 overflow-y-auto font-mono text-sm text-green-400 mb-8" data-testid="voice-transcript">
            {transcript.map((t, i) => (
              <div key={i} className="mb-1.5">
                <span className="text-[var(--t2)]">{t.who}:</span> {t.text}
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <button
              data-testid="voice-mute"
              onClick={() => setMuted((m) => !m)}
              className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            >
              {muted ? <MicOff size={20} /> : <Mic size={20} />}
            </button>
            <button
              data-testid="voice-end"
              onClick={endCall}
              className="w-14 h-14 rounded-full bg-[var(--danger)] flex items-center justify-center text-white"
            >
              <PhoneOff size={20} />
            </button>
            <button
              data-testid="voice-copy"
              onClick={() => {
                navigator.clipboard?.writeText(transcript.map((t) => `${t.who}: ${t.text}`).join("\n"));
                toast.success("Transcript copied");
              }}
              className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
            >
              <Copy size={20} />
            </button>
          </div>
        </div>
      )}

      {/* VIDEO CALL OVERLAY */}
      {videoCall && (
        <div className="fixed inset-0 z-[200] bg-black flex flex-col" data-testid="video-call-overlay">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />
          <div className="relative z-10 flex justify-between items-center p-5">
            <span className="px-3 py-1 rounded-full bg-[var(--accent)]/20 text-[var(--accent)] font-mono text-xs flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[var(--accent)] pulse-dot" /> LIVE · <CallTimer active />
            </span>
            <span className="font-mono text-xs text-white/70" data-testid="video-status">Gemini 2.5 Flash Vision</span>
          </div>
          <div className="relative z-10 mt-auto p-5 w-full">
            <div className="w-full max-w-3xl mx-auto">
            <div className="w-full bg-black/60 border border-white/10 rounded-lg p-3 h-28 overflow-y-auto font-mono text-xs text-green-400 mb-4" data-testid="video-transcript">
              {transcript.map((t, i) => (
                <div key={i} className="mb-1"><span className="text-white/40">{t.who}:</span> {t.text}</div>
              ))}
            </div>
            <div className="flex gap-3 justify-center items-center">
              <button data-testid="video-mute" onClick={() => setMuted((m) => !m)} className="w-11 h-11 shrink-0 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10">{muted ? <MicOff size={18} /> : <Mic size={18} />}</button>
              <button data-testid="video-flip" onClick={flip} className="w-11 h-11 shrink-0 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10"><RefreshCw size={18} /></button>
              <button data-testid="video-analyze" onClick={analyzeFrame} className="px-4 h-11 shrink-0 rounded-full bg-[var(--accent)] text-black font-medium text-xs flex items-center gap-1.5"><ScanLine size={16} /> Analyze</button>
              <button
                data-testid="video-copy"
                onClick={() => { navigator.clipboard?.writeText(transcript.map((t) => `${t.who}: ${t.text}`).join("\n")); toast.success("Copied"); }}
                className="w-11 h-11 shrink-0 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10"
              >
                <Copy size={18} />
              </button>
              <button data-testid="video-end" onClick={closeVideo} className="w-11 h-11 shrink-0 rounded-full bg-[var(--danger)] flex items-center justify-center text-white"><X size={18} /></button>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
